import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { useMutation } from "@apollo/client";
import { MIGRATE_ENTITY, APPROVE_ENTITY_MIGRATION } from "../../../graphql/Admins/mutation";
import toast from "react-hot-toast";

interface MigrationModalProps {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
  currentEntityType: "ORGANIZATION" | "MASJID" | "INDIVIDUAL";
  onMigrationComplete: (targetEntityType: string) => void;
}

const MigrationModal: React.FC<MigrationModalProps> = ({
  open,
  onClose,
  entityId,
  entityName,
  currentEntityType,
  onMigrationComplete,
}) => {
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [migrationId, setMigrationId] = useState<string | null>(null);
  const [migrationStatus, setMigrationStatus] = useState<string>("");
  const [selectedTargetType, setSelectedTargetType] = useState<string>("");

  const [migrateEntity] = useMutation(MIGRATE_ENTITY);
  const [approveMigration] = useMutation(APPROVE_ENTITY_MIGRATION);

  // Get available target entity types (exclude current entity type)
  const getAvailableTargetTypes = () => {
    const allTypes = [
      { value: "ORGANIZATION", label: "Organization" },
      { value: "MASJID", label: "Masjid" },
      { value: "INDIVIDUAL", label: "Individual" }
    ];
    return allTypes.filter(type => type.value !== currentEntityType);
  };

  const availableTargetTypes = getAvailableTargetTypes();
  
  // Set default target type if not selected
  React.useEffect(() => {
    if (open && !selectedTargetType && availableTargetTypes.length > 0) {
      setSelectedTargetType(availableTargetTypes[0].value);
    }
  }, [open, selectedTargetType, availableTargetTypes]);

  const getEntityTypeLabel = (entityType: string) => {
    switch (entityType) {
      case "ORGANIZATION": return "Organization";
      case "MASJID": return "Masjid";
      case "INDIVIDUAL": return "Individual";
      default: return entityType;
    }
  };

  const sourceEntityTypeLabel = getEntityTypeLabel(currentEntityType);
  const targetEntityTypeLabel = selectedTargetType ? getEntityTypeLabel(selectedTargetType) : "";

  const handleMigration = async () => {
    if (!description.trim()) {
      toast.error("Please provide a description for the migration");
      return;
    }

    if (!selectedTargetType) {
      toast.error("Please select a target entity type");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await migrateEntity({
        variables: {
          input: {
            sourceEntityId: entityId,
            sourceEntityType: currentEntityType,
            targetEntityType: selectedTargetType,
            description: description.trim(),
            migrationData: JSON.stringify({
              entityName: entityName,
              migratedBy: "Admin", // You can get this from user context
              migratedAt: new Date().toISOString(),
            }),
          },
        },
      });

      if (data?.migrateEntityV2) {
        console.log("Migration successful:", {
          source: currentEntityType,
          target: selectedTargetType,
          entityName: entityName,
          migrationId: data.migrateEntityV2._id,
          status: data.migrateEntityV2.status
        });
        
        // Store migration details for approval
        setMigrationId(data.migrateEntityV2._id);
        setMigrationStatus(data.migrateEntityV2.status);
        
        toast.success(`Migration initiated! ${entityName} will be moved from ${sourceEntityTypeLabel} to ${targetEntityTypeLabel} after approval.`);
      }
    } catch (error: any) {
      console.error("Migration error:", error);
      let errorMessage = "Failed to migrate entity";
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveMigration = async () => {
    if (!migrationId) return;
    
    setIsSubmitting(true);
    try {
      const { data } = await approveMigration({
        variables: { id: migrationId }
      });
      
      if (data?.approveEntityMigration) {
        console.log("Migration approved:", data.approveEntityMigration);
        toast.success(`Migration approved! ${entityName} has been moved to ${targetEntityTypeLabel}.`);
        onMigrationComplete(selectedTargetType);
        onClose();
      }
    } catch (error: any) {
      console.error("Approval error:", error);
      
      // More detailed error handling
      let errorMessage = "Failed to approve migration";
      let errorDetails = "";
      
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        errorMessage = graphQLError.message;
        
        // Log detailed error info for debugging
        console.log("GraphQL Error Details:", {
          message: graphQLError.message,
          locations: graphQLError.locations,
          path: graphQLError.path,
          extensions: graphQLError.extensions
        });
      }
      
      if (error.networkError) {
        errorDetails = "Network error occurred";
        console.log("Network Error:", error.networkError);
      }
      
      // Handle specific ObjectID validation error
      if (errorMessage.includes("cannot marshal type primitive.M to a BSON Document") || 
          errorMessage.includes("the provided hex string is not a valid ObjectID") ||
          errorMessage.includes("failed to update user entity associations")) {
        
        toast.error("Migration failed due to invalid user data format. Please contact support to fix the user ID format in the database.");
        
        // Reset migration state to allow retry
        setMigrationId(null);
        setMigrationStatus("");
        
        // Don't close the modal, let user try again
        return;
      }
      
      // Show detailed error to user
      toast.error(`${errorMessage}${errorDetails ? ` - ${errorDetails}` : ''}`);
      
      // If it's an internal system error, suggest contacting backend
      if (errorMessage.includes("internal system error")) {
        toast.error("Backend error detected. Please contact your system administrator.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setDescription("");
      setMigrationId(null);
      setMigrationStatus("");
      setSelectedTargetType("");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
        Migrate Entity
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action will move "{entityName}" from{" "}
              {sourceEntityTypeLabel} to {targetEntityTypeLabel}. This action cannot be easily undone.
            </Typography>
          </Alert>
          
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Migration creates a pending request that requires approval. 
              The entity will remain in its current location until the migration is approved.
            </Typography>
          </Alert>
          
          {migrationId && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Migration Created!</strong> ID: {migrationId}<br/>
                Status: {migrationStatus.toUpperCase()}<br/>
                Click "Approve Migration" to complete the move.
              </Typography>
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Source:</strong> {sourceEntityTypeLabel} - {entityName}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Target:</strong> {targetEntityTypeLabel}
          </Typography>
        </Box>

        {!migrationId && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Target Entity Type*</InputLabel>
            <Select
              value={selectedTargetType}
              onChange={(e) => setSelectedTargetType(e.target.value)}
              label="Target Entity Type*"
            >
              {availableTargetTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label="Migration Description*"
          placeholder="Explain why you are migrating this entity..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={3}
          variant="outlined"
          sx={{ mb: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        {!migrationId ? (
          // Initial migration state
          <>
            <Button
              onClick={handleClose}
              disabled={isSubmitting}
              variant="outlined"
              sx={{ minWidth: "100px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMigration}
              disabled={isSubmitting || !description.trim() || !selectedTargetType}
              variant="contained"
              color="primary"
              sx={{ 
                minWidth: "100px",
                bgcolor: "#1B8368",
                "&:hover": { bgcolor: "#156b52" }
              }}
            >
              {isSubmitting ? "Migrating..." : "Migrate"}
            </Button>
          </>
        ) : (
          // After migration, show approval
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ minWidth: "100px" }}
            >
              Close
            </Button>
            <Button
              onClick={handleApproveMigration}
              disabled={isSubmitting}
              variant="contained"
              color="success"
              sx={{ 
                minWidth: "100px",
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" }
              }}
            >
              {isSubmitting ? "Approving..." : "Approve Migration"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MigrationModal; 